import { Text, Group, Stack } from "@mantine/core";
import { BooleanToggle } from "./BooleanToggle";

export interface VisibilityField {
  key: string;
  label: string;
  description?: string;
}

/** All known visibility fields. Consumers pass which subset to show. */
export const VISIBILITY_FIELDS: VisibilityField[] = [
  { key: "doorbell", label: "Visible on doorbell", description: "Show your name when visitors ring the doorbell" },
  { key: "email", label: "Email address", description: "Show your email in the member directory" },
  { key: "phone", label: "Phone number", description: "Show your phone number in the member directory" },
  { key: "company", label: "Company name", description: "Show your company name" },
  { key: "floor", label: "Floor location", description: "Show which floor you're on" },
  { key: "room", label: "Room number", description: "Show your room number" },
  // Entity-level surfaces (Tenant, Floor, etc.)
  { key: "directory", label: "Member directory & lobby kiosks", description: "Show in the member directory and on Launcher kiosk directories" },
  { key: "signage", label: "Digital signage", description: "Show on lobby/elevator signage screens" },
];

export interface VisibilitySettingsProps {
  /** Which fields to show. Defaults to all VISIBILITY_FIELDS. */
  fields?: VisibilityField[];
  /** Show only these field keys (shorthand for filtering VISIBILITY_FIELDS). */
  show?: string[];
  hiddenFrom: string[];
  onUpdate: (hiddenFrom: string[]) => void;
  disabled?: boolean;
  title?: string;
  subtitle?: string;
}

export function VisibilitySettings({
  fields,
  show,
  hiddenFrom,
  onUpdate,
  disabled = false,
  title = "Visibility",
  subtitle = "Control what others can see",
}: VisibilitySettingsProps) {
  const resolvedFields = fields ?? (show ? VISIBILITY_FIELDS.filter((f) => show.includes(f.key)) : VISIBILITY_FIELDS);

  const toggle = (key: string, visible: boolean) => {
    const updated = visible
      ? hiddenFrom.filter((f) => f !== key)
      : [...hiddenFrom.filter((f) => f !== key), key];
    onUpdate(updated);
  };

  return (
    <Stack gap="sm">
      {title && (
        <div>
          <Text fw={600} fz="sm">{title}</Text>
          {subtitle && <Text fz="xs" c="dimmed">{subtitle}</Text>}
        </div>
      )}
      {resolvedFields.map(({ key, label, description }) => {
        const isHidden = hiddenFrom.includes(key);
        return (
          <Group key={key} justify="space-between" wrap="nowrap">
            <div>
              <Text fz="sm" fw={500}>{label}</Text>
              {description && <Text fz="xs" c="dimmed">{description}</Text>}
            </div>
            <BooleanToggle
              value={!isHidden}
              onChange={(visible) => toggle(key, visible)}
              theme="eye"
              disabled={disabled}
              size="xs"
            />
          </Group>
        );
      })}
    </Stack>
  );
}
